import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Circle,
  Popup,
  ZoomControl,
  useMap,
  useMapEvents,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import axiosInstance from "../../config/axiosInterceptor";
import ViewUser from "../User/ViewUser";
import "./DashboardMap.css";

const DEFAULT_CENTER = [20, 0];
const DEFAULT_ZOOM = 2;
const EARTH_RADIUS_KM = 6371;

// REACT_APP_MAP_PROVIDER: "maptiler" | "carto" | "osm" (default: "osm")
// REACT_APP_MAP_API_KEY: shared key, only required for the "maptiler" provider
const MAP_TILE_PROVIDERS = {
  maptiler: {
    url: `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${process.env.REACT_APP_MAP_API_KEY || ""}`,
    attribution:
      '&copy; <a href="https://www.maptiler.com/copyright/">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  carto: {
    url: "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
    attribution:
      '&copy; <a href="https://carto.com/attributions">CARTO</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
  osm: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  },
};

const MAP_TILE_CONFIG =
  MAP_TILE_PROVIDERS[process.env.REACT_APP_MAP_PROVIDER] || MAP_TILE_PROVIDERS.osm;

const haversineDistanceKm = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

// Click to place the center, drag to grow the circle, release to finalize. While drawing,
// map panning is disabled so the drag gesture controls the circle instead of the viewport.
const RadiusDrawer = ({ drawing, onDrawComplete }) => {
  const map = useMap();
  const [center, setCenter] = useState(null);
  const [radiusKm, setRadiusKm] = useState(0);
  const isDrawingRef = useRef(false);

  useEffect(() => {
    if (!drawing) {
      setCenter(null);
      setRadiusKm(0);
      isDrawingRef.current = false;
      map.dragging.enable();
    }
  }, [drawing, map]);

  useMapEvents({
    mousedown(e) {
      if (!drawing) return;
      map.dragging.disable();
      isDrawingRef.current = true;
      setCenter([e.latlng.lat, e.latlng.lng]);
      setRadiusKm(0);
    },
    mousemove(e) {
      if (!drawing || !isDrawingRef.current || !center) return;
      setRadiusKm(haversineDistanceKm(center[0], center[1], e.latlng.lat, e.latlng.lng));
    },
    mouseup() {
      if (!drawing || !isDrawingRef.current) return;
      isDrawingRef.current = false;
      map.dragging.enable();
      if (center && radiusKm > 0) {
        onDrawComplete(center, Math.round(radiusKm));
      }
    },
  });

  if (!center || radiusKm <= 0) return null;
  return <Circle center={center} radius={radiusKm * 1000} pathOptions={{ color: "#329DFF", fillOpacity: 0.08 }} />;
};

// The map mounts inside a flex/animated container whose final width isn't settled on first
// paint (fade-in animation, sidebar collapse, window resize), so Leaflet's initial size
// calculation goes stale and leaves a blank strip. Re-measure whenever the container changes.
const MapResizeHandler = () => {
  const map = useMap();

  useEffect(() => {
    const invalidate = () => map.invalidateSize();
    const raf = requestAnimationFrame(invalidate);
    const timeout = setTimeout(invalidate, 400); // after the fade-in / panel-collapse transition

    window.addEventListener("resize", invalidate);

    const container = map.getContainer().parentElement;
    const observer = new ResizeObserver(invalidate);
    if (container) observer.observe(container);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
      window.removeEventListener("resize", invalidate);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

// Hands the underlying Leaflet map instance up to the parent via a ref, so "locate user"
// (triggered from the left panel, outside the MapContainer tree) can fly to a marker.
const MapRefSetter = ({ mapRef }) => {
  const map = useMap();
  useEffect(() => {
    mapRef.current = map;
  }, [map, mapRef]);
  return null;
};

const LOCATE_ZOOM = 13;

const DashboardMap = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const viewUserId = searchParams.get("id");
  const [points, setPoints] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [radius, setRadius] = useState(null); // { center: [lat, lng], km }
  const [isDrawing, setIsDrawing] = useState(false);
  const [showCountryMenu, setShowCountryMenu] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);
  const [viewUserWidthPct, setViewUserWidthPct] = useState(50);
  const isResizingRef = useRef(false);

  const mapRef = useRef(null);
  const markerRefs = useRef({}); // userId -> Leaflet CircleMarker instance
  const pageRef = useRef(null);

  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("/admin/dashboard/map-countries");
      if (data.success) setCountries(data.countries || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  const fetchPoints = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCountry) params.country = selectedCountry;
      if (activeOnly) params.active = true;
      if (radius) {
        params.lat = radius.center[0];
        params.lng = radius.center[1];
        params.radiusKm = radius.km;
      }
      const { data } = await axiosInstance.get("/admin/dashboard/map-points", { params });
      if (data.success) setPoints(data.points || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, activeOnly, radius]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  // Cache is 2 min server-side; refresh on the same cadence so the view stays current.
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCountries();
      fetchPoints();
    }, 120000);
    return () => clearInterval(interval);
  }, [fetchCountries, fetchPoints]);

  const filteredPoints = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return points;
    return points.filter((p) =>
      [p.username, p.name, p.city, p.region, p.country].some((field) =>
        (field || "").toLowerCase().includes(q)
      )
    );
  }, [points, searchQuery]);

  const totalOnline = useMemo(() => filteredPoints.filter((p) => p.active).length, [filteredPoints]);

  const handleDrawToggle = () => {
    if (isDrawing) {
      setIsDrawing(false);
    } else {
      setRadius(null);
      setIsDrawing(true);
    }
  };

  const handleDrawComplete = (center, km) => {
    setRadius({ center, km });
    setIsDrawing(false);
  };

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    setShowCountryMenu(false);
  };

  const clearFilters = () => {
    setSelectedCountry("");
    setActiveOnly(false);
    setRadius(null);
    setIsDrawing(false);
    setSearchQuery("");
  };

  const openUserPanel = (userId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("id", userId);
      return next;
    });
  };

  const closeUserPanel = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("id");
      return next;
    });
  };

  const VIEWUSER_MIN_WIDTH_PCT = 25;
  const VIEWUSER_MAX_WIDTH_PCT = 50;

  const handleResizeStart = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizingRef.current || !pageRef.current) return;
      const rect = pageRef.current.getBoundingClientRect();
      const pct = ((rect.right - e.clientX) / rect.width) * 100;
      const clamped = Math.min(VIEWUSER_MAX_WIDTH_PCT, Math.max(VIEWUSER_MIN_WIDTH_PCT, pct));
      setViewUserWidthPct(clamped);
    };

    const handleMouseUp = () => {
      if (!isResizingRef.current) return;
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const locateUser = (point) => {
    if (!mapRef.current || point.lat == null || point.lng == null) return;

    mapRef.current.flyTo([point.lat, point.lng], LOCATE_ZOOM, { duration: 0.8 });
    const marker = markerRefs.current[point.userId];
    if (marker) {
      mapRef.current.once("moveend", () => marker.openPopup());
    }
  };

  return (
    <div
      ref={pageRef}
      className={`dashboard-map-page dm-fullscreen-enter ${viewUserId ? "dm-viewuser-open" : ""}`}
    >
      <div className={`dashboard-map-left-panel ${panelCollapsed ? "collapsed" : ""}`}>
        {!panelCollapsed && (
          <>
            <div className="dm-panel-header">
              <h5>Users</h5>
              <button className="dm-collapse-btn" onClick={() => setPanelCollapsed(true)}>
                <i className="fa fa-angle-left" />
              </button>
            </div>
            <div className="dm-search-wrap">
              <i className="fa fa-search dm-search-icon" />
              <input
                type="text"
                className="dm-search-input"
                placeholder="Search by user or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <i className="fa fa-times dm-search-clear" onClick={() => setSearchQuery("")} />
              )}
            </div>
            <div className="dm-panel-tabs">
              <span className="dm-panel-tab dm-panel-tab-active">
                {filteredPoints.length} filtered
              </span>
              <span className="dm-panel-tab">
                <span className="dm-dot dm-dot-online" /> {totalOnline} online
              </span>
            </div>
            <div className="dm-user-list">
              {filteredPoints.map((p) => (
                <div key={p.userId} className="dm-user-row">
                  <img
                    src={p.picture ? `${process.env.REACT_APP_BASEURL1}${p.picture}` : "/img/default-user.png"}
                    alt={p.username}
                    className="dm-user-avatar"
                    onError={(e) => (e.target.style.visibility = "hidden")}
                  />
                  <div className="dm-user-info">
                    <div className="dm-user-name">{p.username || p.name}</div>
                    <div className="dm-user-location">
                      {[p.city, p.region, p.country].filter(Boolean).join(", ")}
                    </div>
                  </div>
                  <button
                    className="dm-locate-btn"
                    title="Locate on map"
                    onClick={() => locateUser(p)}
                  >
                    <i className="fa fa-crosshairs" />
                  </button>
                  <span className={`dm-dot ${p.active ? "dm-dot-online" : "dm-dot-offline"}`} />
                </div>
              ))}
              {!loading && filteredPoints.length === 0 && <p className="dm-empty">No users match the current filter</p>}
              {loading && <p className="dm-empty">Loading...</p>}
            </div>
          </>
        )}
        {panelCollapsed && (
          <button className="dm-expand-btn" onClick={() => setPanelCollapsed(false)}>
            <i className="fa fa-angle-right" />
          </button>
        )}
      </div>

      <div className="dashboard-map-container">
        {/* Floating filter pills, overlaid on the map */}
        <div className="dm-float-controls dm-float-top-left">
          <div className="dm-pill-group">
            <div className="dm-pill-dropdown-wrap">
              <button
                className={`dm-pill ${selectedCountry ? "dm-pill-active" : ""}`}
                onClick={() => setShowCountryMenu((v) => !v)}
              >
                {selectedCountry || "All Countries"}
                <i className="fa fa-caret-down" style={{ marginLeft: 6 }} />
              </button>
              {showCountryMenu && (
                <div className="dm-pill-dropdown">
                  <div className="dm-pill-dropdown-item" onClick={() => handleSelectCountry("")}>
                    All Countries
                  </div>
                  {countries.map((c) => (
                    <div
                      key={c.country}
                      className="dm-pill-dropdown-item"
                      onClick={() => handleSelectCountry(c.country)}
                    >
                      {c.country} <span className="dm-pill-dropdown-count">{c.count}</span>
                    </div>
                  ))}
                  {countries.length === 0 && (
                    <div className="dm-pill-dropdown-item dm-empty">No location data yet</div>
                  )}
                </div>
              )}
            </div>

            <button
              className={`dm-pill ${isDrawing || radius ? "dm-pill-active" : ""}`}
              onClick={handleDrawToggle}
            >
              <i className="fa fa-circle-o" style={{ marginRight: 6 }} />
              {isDrawing ? "Click + drag on map..." : radius ? `${radius.km} km radius` : "Select Area"}
            </button>

            <button
              className={`dm-pill ${activeOnly ? "dm-pill-active" : ""}`}
              onClick={() => setActiveOnly((v) => !v)}
            >
              <span className="dm-dot dm-dot-online" style={{ marginRight: 8 }} />
              Active only
            </button>
          </div>

          {(selectedCountry || radius) && (
            <div className="dm-pill-group">
              <span className="dm-chip">
                {selectedCountry || `${radius.km} km radius`}
                <i className="fa fa-times" onClick={clearFilters} />
              </span>
            </div>
          )}
        </div>

        <div className="dm-float-controls dm-float-top-right dm-float-row">
          <span className="dm-total-badge">
            <span className="dm-live-dot" />
            {filteredPoints.length} users
          </span>
          <button className="dm-exit-btn" onClick={() => navigate("/admin")} title="Exit fullscreen">
            <i className="fa fa-times" />
            Exit
          </button>
        </div>

        {isDrawing && <div className="dm-draw-hint">Click and drag on the map to draw a radius</div>}

        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          zoomControl={false}
          style={{ height: "100%", width: "100%" }}
        >
          <ZoomControl position="bottomright" />
          <MapResizeHandler />
          <MapRefSetter mapRef={mapRef} />
          <TileLayer
            attribution={MAP_TILE_CONFIG.attribution}
            url={MAP_TILE_CONFIG.url}
          />
          <RadiusDrawer drawing={isDrawing} onDrawComplete={handleDrawComplete} />
          {radius && !isDrawing && (
            <Circle
              center={radius.center}
              radius={radius.km * 1000}
              pathOptions={{ color: "#329DFF", fillOpacity: 0.08 }}
            />
          )}
          {filteredPoints.map((p) => (
            <CircleMarker
              key={p.userId}
              ref={(marker) => {
                if (marker) markerRefs.current[p.userId] = marker;
                else delete markerRefs.current[p.userId];
              }}
              center={[p.lat, p.lng]}
              radius={6}
              pathOptions={{
                color: p.active ? "#3CB371" : "#94a3b8",
                fillColor: p.active ? "#3CB371" : "#94a3b8",
                fillOpacity: 0.8,
              }}
            >
              <Popup>
                <strong>{p.username || p.name}</strong>
                <br />
                {[p.city, p.region, p.country].filter(Boolean).join(", ")}
                <br />
                IP: {p.ip}
                <br />
                Status: {p.active ? "Online" : "Offline"}
                <br />
                <span className="dm-popup-view-link" onClick={() => openUserPanel(p.userId)}>
                  View details
                </span>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      {viewUserId && (
        <>
          <div className="dm-viewuser-resize-handle" onMouseDown={handleResizeStart} />
          <div className="dm-viewuser-panel" style={{ width: `${viewUserWidthPct}%` }}>
            <div className="dm-viewuser-panel-header">
              <button className="dm-viewuser-close-btn" onClick={closeUserPanel}>
                <i className="fa fa-times" /> Close
              </button>
            </div>
            <div className="dm-viewuser-panel-body">
              <ViewUser key={viewUserId} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardMap;
